import type { ReactElement, MouseEvent } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ShareIcon from '@/public/images/common/share.svg'
import { AppRoutes } from '@/config/routes'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import type { SafeTransaction } from '@safe-global/safe-core-sdk-types'
import { useShareMagicLink } from '@/hooks/useMagicLink'

const LS_MAGICLINK_ONBOARDING = 'magiclink_onboarding'

const TxShareLink = ({ safeTx }: { safeTx: SafeTransaction }): ReactElement => {
  const tx = useShareMagicLink(safeTx)
  // SAFE4 patch: URL shown for manual copy when programmatic copy is unavailable.
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null)

  const router = useRouter()
  const { safe = '' } = router.query
  const href = `${AppRoutes.transactions.tx}/?safe=${safe}&tx=${tx}`

  // SAFE4 patch: robust Smart Link copy. In restrictive mobile webviews (e.g. the SafeWallet
  // in-app browser) navigator.clipboard.writeText is permission-denied, document.execCommand('copy')
  // returns false, and window.prompt() is a silent no-op. The original code also neither awaited
  // the clipboard promise nor caught its async rejection, so it silently did nothing. Try clipboard,
  // then execCommand, and finally open a dialog showing the full link as selectable text — native
  // long-press "Copy" works even when the JS clipboard API is blocked.
  const copySmartLink = async (text: string): Promise<void> => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return
      }
    } catch (error) {
      console.error(error)
    }
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.top = '0'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.focus()
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      if (ok) return
    } catch (error) {
      console.error(error)
    }
    setFallbackUrl(text)
  }

  const onClick = (e: MouseEvent) => {
    if (!e.ctrlKey && !e.metaKey) {
      e.preventDefault()
    }
    void copySmartLink(location.origin + href)
  }

  return (
    <>
      <Tooltip title="Copy Smart Link" disableInteractive placement="top">
        <IconButton data-testid="share-btn" component={Link} aria-label="Share" href={href} onClick={onClick}>
          <SvgIcon component={ShareIcon} inheritViewBox fontSize="small" color="border" />
        </IconButton>
      </Tooltip>

      <Dialog open={fallbackUrl !== null} onClose={() => setFallbackUrl(null)} fullWidth maxWidth="sm">
        <DialogTitle>Smart Link</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Long-press the link below to select it, then choose Copy. Send it to the other owners so they can sign.
          </Typography>
          <TextField
            value={fallbackUrl ?? ''}
            fullWidth
            multiline
            InputProps={{ readOnly: true }}
            onFocus={(e) => e.target.select()}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

export default TxShareLink
